import { createStore, compose, applyMiddleware } from "redux";
import reduxImmutableStateInvariant from "redux-immutable-state-invariant";
import thunk from "redux-thunk";
// Note: react-router-redux is removed - React Router 7 manages its own state
import { apiMiddleware } from "redux-api-middleware";
import rootReducer from "reducers";
import { dev, testing, staging, production, genericHeaders } from "../middlewares";

import { persistStore, persistReducer, createTransform } from "redux-persist";
import storage from "redux-persist/es/storage/session";

//
// Configure different Middleware environments for dev - prod 
//

// Transform to reset isFetching flags on rehydration
// These shouldn't persist because they represent in-flight requests that are no longer happening
// Also reset allCustomers and allMajorCompanies since they should be fetched fresh each session
const resetFetchingFlags = createTransform(
  // transform state on its way to being serialized and persisted (inbound)
  (inboundState) => inboundState,
  // transform state being rehydrated (outbound)
  (outboundState) => {
    if (!outboundState || typeof outboundState !== "object") return outboundState;
    const resetState = { ...outboundState };
    Object.keys(resetState).forEach(key => {
      if (key.startsWith("isFetching")) {
        resetState[key] = false;
      }
    });
    // Reset large lists that should be fetched fresh
    resetState.allCustomers = null;
    resetState.allMajorCompanies = null;
    return resetState;
  },
  // only apply to these reducers
  { whitelist: ["user"] }
);

// Setup for react-persist
const config = {
  key: "root",
  storage,
  debug: true,
  whitelist: [
    "user",
    "routing",
    "caseDetails",
    "caseForms",
    "filters",
    "internetUser",
    "search",
  ],
  transforms: [resetFetchingFlags],
};
const reducer = persistReducer(config, rootReducer);

// DEVELOPMENT - dev.webapi.mueller-inc.com
function configureStoreDev(initialState) {
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose; // add support for Redux dev tools
  const middlewares = [
    dev,
    genericHeaders,
    apiMiddleware,
    reduxImmutableStateInvariant(),
    thunk,
  ];
  const store = createStore(reducer, initialState, composeEnhancers(applyMiddleware(...middlewares)));
  let persistor = persistStore(store);

  if (module.hot) {
    // Enable Webpack hot module replacement for reducers
    module.hot.accept("../reducers", () => {
      const nextReducer = require("../reducers").default; // eslint-disable-line global-require
      store.replaceReducer(nextReducer);
    });
  }
  return { persistor, store };
}

// TEST - test.webapi.mueller-inc.com
function configureStoreTest(initialState) {
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose; // add support for Redux dev tools
  const middlewares = [
    testing,
    genericHeaders,
    apiMiddleware,
    thunk,
  ];
  let store = createStore(reducer, initialState, composeEnhancers(applyMiddleware(...middlewares)));
  let persistor = persistStore(store);

  return { persistor, store };
}

// STAGING - staging.webapi.mueller-inc.com
function configureStoreStaging(initialState) {
  const middlewares = [
    staging,
    genericHeaders,
    apiMiddleware,
    reduxImmutableStateInvariant(),
    thunk,
  ];
  let store = createStore(reducer, initialState, compose(applyMiddleware(...middlewares)));
  let persistor = persistStore(store);

  return { persistor, store };
}

// PRODUCTION - webapi.mueller-inc.com
function configureStoreProd(initialState) {
  const middlewares = [
    production,
    genericHeaders,
    apiMiddleware,
    reduxImmutableStateInvariant(),
    thunk,
  ];

  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose; // add support for Redux dev tools
  let store = createStore(reducer, initialState, composeEnhancers(applyMiddleware(...middlewares)));
  let persistor = persistStore(store);

  return { persistor, store };
}

function selectStore() {
  switch (process.env.NODE_ENV) {
    case "development":
      return configureStoreDev;

    case "testing":
      return configureStoreTest;

    case "staging":
      return configureStoreStaging;

    case "production":
      return configureStoreProd;

    default:
      return configureStoreDev;
  }
}

const configureStore = selectStore();

export default configureStore;
